import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { usePwcNavigate } from '@app/core/hooks/routing-hooks';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  IRRHurdleCalculationsNavigatedSourcePage,
  SponsorIrrAndHurdleTab,
  StaticCaseSelectionOptions,
} from '../models/contracts/enums/shared-enums';
import { buildFocusModeSearchParams } from '@app/shared/hooks/focus-mode';
import { FocusModeLocationState } from '@app/shared/hooks/focus-mode/focus-mode-types';

interface UseIrrHurdleNavigationParams {
  selectedYear: string;
  sourcePage: IRRHurdleCalculationsNavigatedSourcePage;
}

export const useIrrHurdleNavigation = ({
  selectedYear,
  sourcePage,
}: UseIrrHurdleNavigationParams) => {
  const { generatePath, navigate } = usePwcNavigate();
  const { projectId } = useParams();
  const location = useLocation();
  const selectedCaseId = useAppSelector(
    (state) => state.project.projectDraft.opmInput.selectedCaseId
  );

  return useCallback(
    (instrumentId: string, conditionReference: string, title: string, year?: string) => {
      if (!projectId) {
        return;
      }

      const selections = {
        type: SponsorIrrAndHurdleTab.IRRHurdleCalculations as const,
        case:
          selectedCaseId && selectedCaseId !== StaticCaseSelectionOptions.CaseWeighted
            ? selectedCaseId
            : undefined,
        year: year ?? selectedYear,
        instrument: instrumentId,
        hurdle: conditionReference,
        hurdleTitle: title,
        source: sourcePage,
      };
      const params = buildFocusModeSearchParams(selections);

      const basePath = `/${RouteConstants.Projects}/${projectId}/${RouteConstants.SponsorIRRAndHurdle}/${RouteConstants.IRRHurdleCalculations}`;
      const newPath =
        selectedCaseId && selectedCaseId !== StaticCaseSelectionOptions.CaseWeighted
          ? generatePath(`${basePath}/${selectedCaseId}`)
          : generatePath(`${basePath}`);

      navigate(`${newPath}?${params}`, {
        state: { returnPath: location.pathname } as FocusModeLocationState,
      });
    },
    [selectedYear, sourcePage, selectedCaseId, generatePath, navigate, projectId, location.pathname]
  );
};
